import React from 'react'
import { graphql } from 'gatsby'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'

import Analytics from 'src/utils/Analytics'
import ConditionalCTA from 'src/components/ConditionalCTA'
import LoadingAnimation from 'src/components/LoadingAnimation'
import SEO from 'src/components/seo'
import { BuyNowLink } from 'src/utils/styles/global-styles'
import { BuyNowAnchor } from 'src/components/Hero/styles'
import {
  Section,
  Container,
  Text,
  Header,
  Content,
  BlogImageContainer,
  ButtonContainer,
  BlogPostsContainer,
  BlogImage,
} from '../../utils/styles/singleColumnBlog'

class SingleColumnBlogPage extends React.Component {
  componentDidMount() {
    const { pageContext } = this.props
    if (!pageContext?.name) return
    Analytics.track(`Viewed ${pageContext.name} Blog Post Landing Page`)
  }

  render() {
    const { pageContext, data } = this.props
    const { cal15BoxOgImage } = data

    if (!pageContext?.fields) return <LoadingAnimation />
    const {
      slug,
      fields: {
        seo,
        hero,
        sections,
        footer: { cta },
        indexing,
      },
    } = pageContext

    // Property must match slug
    const metaMap = {
      'skylight-affiliate-partner-program': {
        title: 'Skylight Calendar | Affiliates | Partner Program',
        description:
          'Join Skylight’s affiliate partner program! Earn commissions for telling your audience all about how Skylight changes the way families and friends share moments.',
        imagePath: cal15BoxOgImage.childImageSharp.gatsbyImageData.images.fallback.src,
      },
      fallback: {
        title: seo.title,
        description: seo.meta_description,
        path: `/${slug}`,
      },
    }

    const activeSEO = { ...metaMap.fallback, ...metaMap[slug] }
    return (
      <Section>
        <SEO
          title={activeSEO.title}
          description={activeSEO.description}
          path={activeSEO.path}
          imagePath={activeSEO.imagePath}
        />
        {!indexing && (
          <Helmet>
            <meta name="robots" content="noindex" />
          </Helmet>
        )}
        <Container>
          <Text>
            <Header>{hero.headline}</Header>
            <p>{hero.sub_headline}</p>
          </Text>
          <BlogPostsContainer>
            {sections.map((section, i) => (
              <Content key={i}>
                <>
                  {sections[i].image ? (
                    <>
                      <BlogImageContainer>
                        <BlogImage src={sections[i].image} alt={sections[i].image_title} />
                      </BlogImageContainer>
                      <Text left dangerouslySetInnerHTML={{ __html: section.text }} />
                    </>
                  ) : (
                    <Text margin="auto" left dangerouslySetInnerHTML={{ __html: section.text }} />
                  )}
                  {sections[i].buy_now_button && (
                    <ButtonContainer>
                      <ConditionalCTA
                        url={hero.cta_button_link}
                        text={hero.cta_button_text}
                        anchorComponent={BuyNowAnchor}
                        linkComponent={BuyNowLink}
                      />
                    </ButtonContainer>
                  )}
                </>
              </Content>
            ))}
            {cta && (
              <ButtonContainer>
                <ConditionalCTA
                  url={hero.cta_button_link}
                  text={cta}
                  anchorComponent={BuyNowAnchor}
                  linkComponent={BuyNowLink}
                />
              </ButtonContainer>
            )}
          </BlogPostsContainer>

          <br />
        </Container>
      </Section>
    )
  }
}

SingleColumnBlogPage.propTypes = {
  data: PropTypes.shape({
    cal15BoxOgImage: PropTypes.object.isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    fields: PropTypes.shape({
      seo: PropTypes.shape({
        title: PropTypes.string.isRequired,
        meta_description: PropTypes.string.isRequired,
      }),
      hero: PropTypes.shape({
        headline: PropTypes.string.isRequired,
        cta_button_link: PropTypes.string.isRequired,
        cta_button_text: PropTypes.string.isRequired,
        sub_headline: PropTypes.string,
      }),
      sections: PropTypes.arrayOf(
        PropTypes.shape({
          image: PropTypes.string,
          image_title: PropTypes.string,
          text: PropTypes.string.isRequired,
          buy_now_button: PropTypes.string,
        })
      ),
      footer: PropTypes.shape({
        cta: PropTypes.string,
      }),
      indexing: PropTypes.bool.isRequired,
    }),
  }),
}

export default SingleColumnBlogPage

export const query = graphql`
  query {
    cal15BoxOgImage: file(relativePath: { eq: "meta-seo/cal-15-box.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
      }
    }
  }
`
